import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircle from "@mui/icons-material/CheckCircle";
import DownloadIcon from "@mui/icons-material/Download";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { Button, CircularProgress, Grid, Link, Stack, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { extensionFromName } from "components/Document/shared/util/extensionFromName";
import { Dropzone } from "components/Dropzone";
import { DisplayOnlyField } from "components/Forms/DisplayOnlyField";
import { DocumentType } from "generated-graphql/graphql";
import { useDeleteDocumentMutation } from "hooks/documents";
import { useDocumentUpload } from "hooks/useDocumentUpload";
import { useTenant } from "hooks/useTenant";
import { useCallback, useState } from "react";

import { useAttachWasteProfileDocument, useGenerateWasteProfileSummary } from "./api";
import { prettyPrintMissingField } from "./profileDisplay";
import { ProfileSection } from "./ProfileSection";
import { WasteStreamProfileData } from "./types";

const FIELD = { xs: 12, sm: 6, lg: 3 } as const;

const orBlank = (value?: string | null): string | undefined => value || undefined;

const formatUpdatedAt = (value?: string | null): string | null => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleString();
};

/**
 * Borrows the Discrepancies section's status idiom — a small leading icon and
 * secondary body2 text — rather than a boxed Alert, which reads as a callout
 * layered on a card that is already a callout. `complete` and `missing` are
 * both server-derived, so this only renders what it is told.
 */
const CompletenessLine = ({
  completeness,
}: {
  completeness?: { complete: boolean; missing: string[] } | null;
}) => {
  if (!completeness) return null;

  if (completeness.complete) {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <CheckCircle sx={{ fontSize: "1.125rem", color: "success.main", flexShrink: 0 }} />
        <Typography variant="body2" color="text.secondary">
          Profile complete
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <ErrorOutline sx={{ fontSize: "1.125rem", color: "warning.main", flexShrink: 0 }} />
      <Typography variant="body2" color="text.secondary">
        Still needed: {completeness.missing.map(prettyPrintMissingField).join(", ")}
      </Typography>
    </Stack>
  );
};

export const ProfileDocumentSection = ({
  wasteStream,
}: {
  wasteStream: WasteStreamProfileData;
}) => {
  const profile = wasteStream.profile ?? null;
  const document = profile?.profileDocument ?? null;
  const alerts = useAlerts();
  const { tenantId } = useTenant();

  const [attachDocument, { loading: attaching }] = useAttachWasteProfileDocument();
  const [deleteDocument] = useDeleteDocumentMutation();
  const [generateSummary, { loading: generating }] = useGenerateWasteProfileSummary();
  const [confirmReplaceOpen, setConfirmReplaceOpen] = useState(false);

  /**
   * Upload and attach are two steps by design: `upsertDocument` creates the
   * Document row and the S3 object, then `attachWasteProfileDocument` points
   * the profile at it and flips the document type. The hook reports the
   * created row through `onSuccess`, so the attach is wired there.
   */
  const { uploadFile, isUploading } = useDocumentUpload({
    onError: (error) => alerts.error("Failed to upload profile document", error.message),
    onSuccess: async (uploaded) => {
      if (!uploaded) return;
      try {
        await attachDocument({
          variables: { wasteStreamId: wasteStream.id, documentId: uploaded.id },
        });
        alerts.success("Profile document attached");
      } catch (error) {
        alerts.error(
          "Failed to attach profile document",
          error instanceof Error ? error.message : undefined
        );
        // The upload already created the Document row and the S3 object, and
        // nothing points at them now. Without this the user retries and every
        // failed attempt leaves another document behind in their Documents
        // list. A failed cleanup is not worth a second error toast on top of
        // the one they already have.
        try {
          await deleteDocument({ variables: { id: uploaded.id } });
        } catch {
          // Intentionally ignored, see above.
        }
      }
    },
  });

  // `attaching` has to be in here alongside the upload: `useDocumentUpload`
  // calls `onSuccess` without awaiting it, so `isUploading` goes false the
  // moment the Document row exists, while the attach that points the profile
  // at it is still in flight. Without this, the dropzone and Generate summary
  // re-enable mid-attach, and every one of those actions writes the same
  // single profile-document slot — the winner would be whichever request
  // finished last, not the action the user took last.
  const busy = isUploading || attaching || generating;

  const onDrop = useCallback(
    (files: File[]) => {
      const file = files[0];
      if (!file) return;
      if (!tenantId) {
        alerts.error("Cannot upload without a tenant");
        return;
      }
      void uploadFile({
        file,
        currentSubmissionDocument: false,
        documentData: {
          tenantId,
          title: file.name,
          fileExtension: extensionFromName(file.name),
          documentType: DocumentType.WasteStreamProfile,
        },
      });
    },
    [alerts, tenantId, uploadFile]
  );

  const runGenerate = useCallback(async () => {
    setConfirmReplaceOpen(false);
    try {
      await generateSummary({ variables: { wasteStreamId: wasteStream.id } });
      alerts.success("Profile summary generated");
    } catch (error) {
      alerts.error(
        "Failed to generate profile summary",
        error instanceof Error ? error.message : undefined
      );
    }
  }, [alerts, generateSummary, wasteStream.id]);

  /**
   * Attach and generate write the same single slot, last write wins, so
   * generating over an existing sheet is confirmed first: the replaced row
   * survives in the database but nothing in this UI can reach it again.
   */
  const handleGenerateClick = () => {
    if (document) {
      setConfirmReplaceOpen(true);
      return;
    }
    void runGenerate();
  };

  const updatedAt = formatUpdatedAt(document?.updatedAt);

  return (
    <ProfileSection
      title="Profile & document"
      action={
        <Button
          size="small"
          startIcon={generating ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
          onClick={handleGenerateClick}
          disabled={busy || !profile}
        >
          Generate summary
        </Button>
      }
    >
      <Stack spacing={2}>
        <Grid container spacing={2} sx={{ alignItems: "flex-start" }}>
          <Grid size={FIELD}>
            <DisplayOnlyField
              label="Profile number"
              text={orBlank(profile?.profileNumber)}
              copyable
            />
          </Grid>
          <Grid size={FIELD}>
            <DisplayOnlyField label="TSDF" text={orBlank(profile?.tsdfName)} copyable />
          </Grid>
          <Grid size={FIELD}>
            <DisplayOnlyField
              label="TSDF profile ID"
              text={orBlank(profile?.tsdfProfileId)}
              copyable
            />
          </Grid>
        </Grid>

        <CompletenessLine completeness={profile?.completeness} />

        {document ? (
          // No border or surface of its own: the section card is already a
          // surface, and the drawer's other in-card rows are plain.
          <Stack direction="row" spacing={1} alignItems="center">
            <InsertDriveFileIcon sx={{ fontSize: "1.125rem", color: "action.active" }} />
            <Stack sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                {document.title}
              </Typography>
              {updatedAt && (
                <Typography variant="caption" color="text.secondary">
                  Last updated {updatedAt}
                </Typography>
              )}
            </Stack>
            {document.downloadLink && (
              <Button
                size="small"
                startIcon={<DownloadIcon />}
                component={Link}
                href={document.downloadLink}
                target="_blank"
                rel="noopener"
              >
                Download
              </Button>
            )}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No profile document yet. Upload the TSDF profile sheet, or generate an Encamp summary
            from the characterization above.
          </Typography>
        )}

        <Dropzone
          label={document ? "Replace the profile document" : "Upload the TSDF profile sheet"}
          dragActiveText="Drop the profile sheet here"
          multiple={false}
          disabled={busy}
          onDrop={onDrop}
        />
      </Stack>

      <ConfirmDialog
        open={confirmReplaceOpen}
        onClose={() => setConfirmReplaceOpen(false)}
        onConfirm={runGenerate}
        header="Replace the current profile document?"
        info="This waste stream has one profile document slot. Generating an Encamp summary replaces what is there now."
        resources={[{ resourceType: "Current document", resourceName: document?.title ?? "" }]}
        confirmText="Yes, generate summary"
        cancelText="Cancel"
        loading={generating}
      />
    </ProfileSection>
  );
};
